import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCreateAgent } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { CreateAgentRequest } from '@/types'
import { Check } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

const NAME_MAX = 256
const DESCRIPTION_MAX = 2000
const INITIAL_VERSION = '1.0.0'

const STARTER_YAML = `systemPrompt: |
  Describe what this agent does and how it should behave.
model: sonnet-4-6
effort: medium
memory: none
skills: []
tools: []
`

interface CreateAgentDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface CreateAgentFormValues {
  displayName: string
  internalName: string
  description: string
}

const KEBAB_CASE = /^[a-z0-9]+(-[a-z0-9]+)*$/

function toSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function CreateAgentDialog({ projectId, open, onOpenChange }: CreateAgentDialogProps) {
  const navigate = useNavigate()
  const createAgent = useCreateAgent(projectId)
  const [internalNameTouched, setInternalNameTouched] = useState(false)
  const { register, handleSubmit, reset, control, setValue, formState: { errors } } = useForm<CreateAgentFormValues>({
    values: {
      displayName: '',
      internalName: '',
      description: '',
    },
  })

  const displayNameValue = useWatch({ control, name: 'displayName' })
  const derivedSlug = toSlug(displayNameValue ?? '')

  useEffect(() => {
    if (!internalNameTouched) setValue('internalName', derivedSlug)
  }, [derivedSlug, internalNameTouched, setValue])

  const onSubmit = async (data: CreateAgentFormValues) => {
    const name = data.internalName.trim()
    try {
      const description = data.description?.trim()
      const payload: CreateAgentRequest = {
        name,
        displayName: data.displayName.trim(),
        yaml: STARTER_YAML,
        ...(description ? { description } : {}),
      }
      const response = await createAgent.mutateAsync(payload)
      toast.success('Agent created successfully')
      reset()
      onOpenChange(false)
      navigate(`/projects/${projectId}/workflows/agents/${response.agentId}/config`)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create agent')
    }
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      reset()
      setInternalNameTouched(false)
    }
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Create New Agent</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="create-agent-displayName" className={labelClass}>Display Name <span className="text-red-500">*</span></Label>
              <Input
                id="create-agent-displayName"
                {...register('displayName', {
                  required: 'Display name is required',
                  maxLength: { value: NAME_MAX, message: `Display name must not exceed ${NAME_MAX} characters` },
                })}
                placeholder="e.g. Backend Analyst"
              />
              {errors.displayName && <p className="text-sm text-destructive" role="alert">{errors.displayName.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-agent-internalName" className={labelClass}>Internal Name <span className="text-red-500">*</span></Label>
              <Input
                id="create-agent-internalName"
                {...register('internalName', {
                  required: 'Internal name is required',
                  maxLength: { value: NAME_MAX, message: `Internal name must not exceed ${NAME_MAX} characters` },
                  pattern: { value: KEBAB_CASE, message: 'Must be kebab-case (e.g. "backend-analyst")' },
                  onChange: () => setInternalNameTouched(true),
                })}
                placeholder="e.g. backend-analyst"
                className="font-mono"
                data-testid="agent-internal-name-input"
              />
              {errors.internalName
                ? <p className="text-sm text-destructive" role="alert">{errors.internalName.message}</p>
                : <p className="text-xs text-muted-foreground">Prefilled from the display name. Workflows reference the agent by this name.</p>}
            </div>
          </div>
          <div className="w-[200px] space-y-2">
            <Label htmlFor="create-agent-version" className={labelClass}>Version</Label>
            <Input
              id="create-agent-version"
              value={INITIAL_VERSION}
              readOnly
              disabled
              className="font-mono"
              data-testid="agent-initial-version"
            />
            <p className="text-xs text-muted-foreground">Assigned automatically on save.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-agent-description" className={labelClass}>Description</Label>
            <Textarea
              id="create-agent-description"
              {...register('description', {
                maxLength: { value: DESCRIPTION_MAX, message: `Description must not exceed ${DESCRIPTION_MAX} characters` },
              })}
              placeholder="One line on what this agent does and when to use it."
              rows={3}
            />
            {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
          </div>
          <p className="text-xs text-muted-foreground">
            The agent starts with a default configuration. You'll be taken to its page to configure the prompt, model, skills, and tools.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>Cancel</Button>
            <Button type="submit" disabled={createAgent.isPending}>
              <Check className="h-4 w-4" />
              {createAgent.isPending ? 'Creating...' : 'Create Agent'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
